import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AlertStatusesModalComponent } from '../../components/alert-statuses-modal/alert-statuses-modal.component';
import { AlertRecipientsModalComponent } from '../../components/alert-recipients-modal/alert-recipients-modal.component';
import { GoogleMapComponent, MapMarker } from '../../components/google-map/google-map.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { Icons } from '../../../shared/helpers/icons';
import { ALERT_CATEGORY_MAP, ALERT_TYPES, RECEIVER_TYPE_LABELS, ResponseCode, RESPONSE_LABELS } from '../../../shared/models/alert.model';
import { AlertLog, AlertRecipient, AlertUserStatus, LockdownUserService, UserStatusLog } from '../../../shared/services/lockdown-user.service';
import { UserDataService } from '../../../shared/services/auth/userData.service';

interface CalendarCell {
  day:            number | null;
  lockdown:       number;
  fire:           number;
  police:         number;
  medical:        number;
  administrative: number;
  test:           number;
}

@Component({
  selector: 'app-activity',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, NgxPaginationModule, AlertStatusesModalComponent, AlertRecipientsModalComponent, GoogleMapComponent, SpinnerComponent],
  templateUrl: './activity.component.html'
})
export class ActivityComponent implements OnInit, OnDestroy {
  icons = Icons;

  readonly today = new Date().toISOString().split('T')[0];

  startDate = (() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().split('T')[0]; })();
  endDate   = new Date().toISOString().split('T')[0];

  readonly weekDays          = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly responseLabels    = RESPONSE_LABELS;
  readonly alertTypeOptions: [string, string][] = [
    ['4', 'Lockdown'], ['2', 'Fire'], ['3', 'Police'], ['1', 'Medical'], ['5', 'Administrative']
  ];
  readonly statusTypeOptions = Object.entries(RESPONSE_LABELS) as [string, string][];

  calendarMonth = new Date().getMonth();
  calendarYear  = new Date().getFullYear();

  loading = false;
  private destroy$ = new Subject<void>();

  alerts:    AlertLog[]      = [];
  responses: UserStatusLog[] = [];

  alertsPage    = 1;
  responsesPage = 1;
  readonly tableItemsPerPage = 10;

  selectedAlert:    AlertLog | null = null;
  alertStatuses:    AlertUserStatus[] = [];
  loadingStatuses   = false;

  selectedAlertForRecipients: AlertLog | null = null;
  alertRecipients:            AlertRecipient[] = [];
  loadingRecipients           = false;

  alertsSearch      = '';
  filterAlertType   = '';
  filterBuildingId  = '';
  alertsSortCol:    keyof AlertLog | null = null;
  alertsSortDir:    'asc' | 'desc' = 'asc';
  filteredAlerts:     AlertLog[] = [];
  dateFilteredAlerts: AlertLog[] = [];
  preFilteredAlerts:  AlertLog[] = [];
  buildingOptions:    { id: string; name: string }[] = [];

  responsesSearch    = '';
  filterStatusType   = '';
  responsesSortCol:  keyof UserStatusLog | null = null;
  responsesSortDir:  'asc' | 'desc' = 'asc';
  filteredResponses: UserStatusLog[] = [];

  constructor(
    private lockdownUserService:   LockdownUserService,
    protected userDataService:     UserDataService,
    private toastr:                ToastrService
  ) {}

  ngOnInit(): void {
    const buildingId = this.userDataService.buildingId();
    if (buildingId) {
      this.loading = true;
      forkJoin([
        this.lockdownUserService.getAlerts(buildingId),
        this.lockdownUserService.getUserStatuses(buildingId)
      ]).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ([alerts, statuses]) => {
            this.alerts    = alerts;
            this.responses = statuses;
            this.loading   = false;
            this.filterAlerts();
            this.filterResponses();
          },
          error: (err: Error) => {
            this.loading = false;
            this.toastr.error(err.message, 'Failed to load activity data');
            this.filterAlerts();
            this.filterResponses();
          }
        });
    } else {
      this.filterAlerts();
      this.filterResponses();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDateRangeChange(): void {
    this.filterAlerts();
    this.filterResponses();
  }

  onTypeFilterChange(): void {
    this.filterAlerts();
    this.filterResponses();
  }

  onBuildingFilterChange(): void {
    this.filterAlerts();
    this.filterResponses();
  }

  trackByBuildingId(_: number, b: { id: string; name: string }): string {
    return b.id;
  }

  openAlertStatuses(alert: AlertLog): void {
    this.selectedAlert  = alert;
    this.alertStatuses  = [];
    this.loadingStatuses = true;
    const buildingId = this.userDataService.buildingId();
    if (!buildingId) { this.loadingStatuses = false; return; }
    this.lockdownUserService.getUserStatusesForAlert(buildingId, alert.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: statuses => { this.alertStatuses = statuses; this.loadingStatuses = false; },
        error: ()      => { this.loadingStatuses = false; }
      });
  }

  closeAlertStatuses(): void {
    this.selectedAlert = null;
    this.alertStatuses = [];
  }

  openAlertRecipients(alert: AlertLog): void {
    this.selectedAlertForRecipients = alert;
    this.alertRecipients = [];
    this.loadingRecipients = true;
    this.lockdownUserService.getRecipientsForAlert(alert.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: recipients => { this.alertRecipients = recipients; this.loadingRecipients = false; },
        error: ()         => { this.loadingRecipients = false; }
      });
  }

  closeAlertRecipients(): void {
    this.selectedAlertForRecipients = null;
    this.alertRecipients = [];
  }

  alertTypeName(typeId: number): string {
    return ALERT_TYPES[typeId] ?? 'Unknown';
  }

  receiverTypeName(typeId: number): string {
    return RECEIVER_TYPE_LABELS[typeId] ?? 'Unknown';
  }

  alertTypeIdForAlert(alertId: number): number {
    return this.alerts.find(a => a.id === alertId)?.alertTypeId ?? 0;
  }

  alertBuildingForResponse(alertId: number): { id: number; name: string } | null {
    const a = this.alerts.find(a => a.id === alertId);
    return a ? { id: a.alertBuildingId, name: a.alertBuildingName } : null;
  }

  prevMonth(): void {
    if (this.calendarMonth === 0) { this.calendarMonth = 11; this.calendarYear--; }
    else { this.calendarMonth--; }
  }

  nextMonth(): void {
    if (this.calendarMonth === 11) { this.calendarMonth = 0; this.calendarYear++; }
    else { this.calendarMonth++; }
  }

  get calendarMonthLabel(): string {
    return new Date(this.calendarYear, this.calendarMonth, 1)
      .toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  get calendarGrid(): CalendarCell[][] {
    const daysInMonth    = new Date(this.calendarYear, this.calendarMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(this.calendarYear, this.calendarMonth, 1).getDay();

    const dayMap = new Map<number, CalendarCell>();
    this.dateFilteredAlerts.forEach(a => {
      const d = new Date(a.dateSubmitted.replace(' ', 'T'));
      if (d.getFullYear() !== this.calendarYear || d.getMonth() !== this.calendarMonth) return;
      const day = d.getDate();
      const cell = dayMap.get(day) ?? { day, lockdown: 0, fire: 0, police: 0, medical: 0, administrative: 0, test: 0 };
      if (!ActivityComponent.MAIN_ALERT_TYPE_IDS.has(a.alertTypeId)) {
        cell.test++;
      } else {
        const cat = ALERT_CATEGORY_MAP[a.alertTypeId];
        if (cat) cell[cat]++;
      }
      dayMap.set(day, cell);
    });

    const cells: CalendarCell[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push({ day: null, lockdown: 0, fire: 0, police: 0, medical: 0, administrative: 0, test: 0 });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(dayMap.get(d) ?? { day: d, lockdown: 0, fire: 0, police: 0, medical: 0, administrative: 0, test: 0 });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ day: null, lockdown: 0, fire: 0, police: 0, medical: 0, administrative: 0, test: 0 });
    }

    const weeks: CalendarCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  cellTotal(cell: CalendarCell): number {
    return cell.lockdown + cell.fire + cell.police + cell.medical + cell.administrative + cell.test;
  }

  cellBgClass(cell: CalendarCell): string {
    const t = this.cellTotal(cell);
    if (t === 0) return 'bg-white';
    if (t <= 2)  return 'bg-orange-50';
    return 'bg-orange-100';
  }

  private static readonly MAIN_ALERT_TYPE_IDS = new Set([1, 2, 3, 4, 5]);

  get alertTotals() {
    const t = { lockdown: 0, fire: 0, police: 0, medical: 0, administrative: 0, test: 0 };
    this.dateFilteredAlerts.forEach(a => {
      if (!ActivityComponent.MAIN_ALERT_TYPE_IDS.has(a.alertTypeId)) { t.test++; return; }
      const cat = ALERT_CATEGORY_MAP[a.alertTypeId];
      if (cat) t[cat]++;
    });
    return t;
  }

  get totalAlerts(): number {
    const t = this.alertTotals;
    return t.lockdown + t.fire + t.police + t.medical + t.administrative + t.test;
  }

  get donutGradient(): string {
    const t     = this.alertTotals;
    const total = this.totalAlerts;
    if (total === 0) return 'conic-gradient(#e5e7eb 0deg 360deg)';
    const lEnd = (t.lockdown            / total) * 360;
    const fEnd = lEnd + (t.fire         / total) * 360;
    const pEnd = fEnd + (t.police       / total) * 360;
    const mEnd = pEnd + (t.medical      / total) * 360;
    const aEnd = mEnd + (t.administrative / total) * 360;
    return `conic-gradient(#ef4444 0deg ${lEnd}deg, #f97316 ${lEnd}deg ${fEnd}deg, #3b82f6 ${fEnd}deg ${pEnd}deg, #22c55e ${pEnd}deg ${mEnd}deg, #a855f7 ${mEnd}deg ${aEnd}deg, #d1d5db ${aEnd}deg 360deg)`;
  }

  filterAlerts(): void {
    const q    = this.alertsSearch.toLowerCase();
    const from = this.startDate;
    const to   = this.endDate;
    const type = this.filterAlertType;
    const building = this.filterBuildingId;
    this.preFilteredAlerts = this.alerts.filter(a => {
      const date = a.dateSubmitted.substring(0, 10);
      if (date < from || date > to) return false;
      if (type && String(a.alertTypeId) !== type) return false;
      return true;
    });

    const seen = new Set<number>();
    this.buildingOptions = [];
    for (const a of this.preFilteredAlerts) {
      if (!seen.has(a.alertBuildingId)) {
        seen.add(a.alertBuildingId);
        this.buildingOptions.push({ id: String(a.alertBuildingId), name: a.alertBuildingName });
      }
    }
    if (building && !this.buildingOptions.some(b => b.id === building)) {
      this.filterBuildingId = '';
    }

    this.dateFilteredAlerts = this.filterBuildingId
      ? this.preFilteredAlerts.filter(a => String(a.alertBuildingId) === this.filterBuildingId)
      : this.preFilteredAlerts;
    this.filteredAlerts = this.dateFilteredAlerts.filter(a =>
      !q ||
      (a.dateSubmitted ?? '').toLowerCase().includes(q) ||
      this.alertTypeName(a.alertTypeId).toLowerCase().includes(q) ||
      (a.userNameSubmitted ?? '').toLowerCase().includes(q) ||
      (a.alertBuildingName ?? '').toLowerCase().includes(q)
    );
    this.applyAlertsSort();
    this.alertsPage = 1;
  }

  sortAlerts(col: keyof AlertLog): void {
    this.alertsSortDir = this.alertsSortCol === col && this.alertsSortDir === 'asc' ? 'desc' : 'asc';
    this.alertsSortCol = col;
    this.applyAlertsSort();
  }

  private applyAlertsSort(): void {
    if (!this.alertsSortCol) return;
    const col = this.alertsSortCol;
    const dir = this.alertsSortDir === 'asc' ? 1 : -1;
    this.filteredAlerts.sort((a, b) => String(a[col]).localeCompare(String(b[col])) * dir);
  }

  filterResponses(): void {
    const q        = this.responsesSearch.toLowerCase();
    const from     = this.startDate;
    const to       = this.endDate;
    const status   = this.filterStatusType;
    const type     = this.filterAlertType;
    const building = this.filterBuildingId;
    this.filteredResponses = this.responses.filter(r => {
      if (!r.dateTimeSubmitted) return false;
      const date = r.dateTimeSubmitted.substring(0, 10);
      if (date < from || date > to) return false;
      if (status && String(r.userStatus) !== status) return false;
      if (type && String(this.alertTypeIdForAlert(r.alertId)) !== type) return false;
      if (building && String(this.alertBuildingForResponse(r.alertId)?.id) !== building) return false;
      if (!q) return true;
      return (
        (r.userName ?? '').toLowerCase().includes(q) ||
        this.alertTypeName(this.alertTypeIdForAlert(r.alertId)).toLowerCase().includes(q) ||
        (r.userStatusName ?? '').toLowerCase().includes(q) ||
        r.dateTimeSubmitted.toLowerCase().includes(q)
      );
    });
    this.applyResponsesSort();
    this.responsesPage = 1;
  }

  sortResponses(col: keyof UserStatusLog): void {
    this.responsesSortDir = this.responsesSortCol === col && this.responsesSortDir === 'asc' ? 'desc' : 'asc';
    this.responsesSortCol = col;
    this.applyResponsesSort();
  }

  private applyResponsesSort(): void {
    if (!this.responsesSortCol) return;
    const col = this.responsesSortCol;
    const dir = this.responsesSortDir === 'asc' ? 1 : -1;
    this.filteredResponses.sort((a, b) => String(a[col]).localeCompare(String(b[col])) * dir);
  }

  get mapMarkers(): MapMarker[] {
    return this.filteredAlerts
      .filter(a => a.latitude && a.longitude)
      .map(a => ({
        lat:      parseFloat(a.latitude),
        lng:      parseFloat(a.longitude),
        title:    a.dateSubmitted,
        type:     this.alertTypeName(a.alertTypeId),
        building: a.buildingLevelName ?? a.alertBuildingName
      }));
  }

  async exportPdf(): Promise<void> {
    const img = new Image();
    img.src = '/assets/logo.png';
    await new Promise(resolve => { img.onload = resolve; });

    const canvas = document.createElement('canvas');
    canvas.width  = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    const logoBase64 = canvas.toDataURL('image/png');
    const logoW      = 2.8;
    const logoH      = logoW / (img.naturalWidth / img.naturalHeight);

    const doc    = new jsPDF();
    const pageW  = doc.internal.pageSize.getWidth();
    const pageH  = doc.internal.pageSize.getHeight();
    const margin = 8;

    doc.setFontSize(16);
    doc.text('nSide | Lockdown — Activity Report', 14, 16);
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(`${this.startDate}  →  ${this.endDate}`, 14, 23);
    doc.text(`Generated ${new Date().toLocaleString()}`, 14, 28);
    doc.setTextColor(0);

    doc.setFontSize(12);
    doc.text('Alert Log', 14, 36);
    autoTable(doc, {
      startY: 39,
      head: [['No.', 'Type', 'Building', 'Sender', 'Date Submitted']],
      body: this.filteredAlerts.map((a, i) => [
        i + 1,
        this.alertTypeName(a.alertTypeId),
        a.alertBuildingName,
        a.userNameSubmitted,
        a.dateSubmitted
      ]),
      headStyles: { fillColor: [230, 90, 30] },
      styles: { fontSize: 9 },
    });

    const afterAlerts = (doc as any).lastAutoTable.finalY + 8;
    doc.setFontSize(12);
    doc.text('User Response Log', 14, afterAlerts);
    const responseHead = this.userDataService.isOrg()
      ? [['No.', 'User', 'Building', 'Status', 'Date Submitted']]
      : [['No.', 'User', 'Status', 'Date Submitted']];
    const responseBody = this.filteredResponses.map((r, i) => {
      const building = this.alertBuildingForResponse(r.alertId)?.name ?? '—';
      return this.userDataService.isOrg()
        ? [i + 1, r.userName, building, r.userStatusName, r.dateTimeSubmitted]
        : [i + 1, r.userName, r.userStatusName, r.dateTimeSubmitted];
    });

    autoTable(doc, {
      startY: afterAlerts + 3,
      head: responseHead,
      body: responseBody,
      headStyles: { fillColor: [230, 90, 30] },
      styles: { fontSize: 9 },
    });

    const total = doc.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      doc.setPage(p);
      doc.addImage(logoBase64, 'PNG', pageW - logoW - margin, pageH - logoH - margin, logoW, logoH);
    }

    doc.save(`activity-report-${this.startDate}-${this.endDate}.pdf`);
  }

  alertPercent(value: number): string {
    if (this.totalAlerts === 0) return '0%';
    return `${Math.round((value / this.totalAlerts) * 100)}%`;
  }

  alertTypeClass(typeId: number): string {
    if (!ActivityComponent.MAIN_ALERT_TYPE_IDS.has(typeId)) return 'bg-gray-100 text-gray-600';
    const category = ALERT_CATEGORY_MAP[typeId] ?? 'lockdown';
    switch (category) {
      case 'lockdown':       return 'bg-red-100 text-red-600';
      case 'fire':           return 'bg-orange-100 text-orange-600';
      case 'police':         return 'bg-blue-100 text-blue-600';
      case 'medical':        return 'bg-green-100 text-green-600';
      case 'administrative': return 'bg-purple-100 text-purple-600';
      default:               return 'bg-gray-100 text-gray-600';
    }
  }

  responseClass(code: ResponseCode): string {
    switch (code) {
      case 1: return 'bg-green-100 text-green-600';
      case 2: return 'bg-amber-100 text-amber-600';
      case 3: return 'bg-blue-100 text-blue-600';
      case 4: return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  }
}
